import axios from "axios";

import { trackEvent } from "@/services/analytics";
import { captureApiError } from "@/services/monitoring";
import { useAuthStore } from "@/stores/auth";

import {
  buildServiceUnavailableUrl,
  classifyApiFailure,
  isApiReachable,
  reasonFromResponse,
  SERVICE_UNAVAILABLE_PATH,
  type ServiceUnavailableReason,
} from "./serviceUnavailable";

const apiBaseURL: string = import.meta.env.VITE_API_URL || "/api";

// Create an Axios instance with defaults
const apiClient = axios.create({
  baseURL: apiBaseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Reduce a request URL to a PII-safe endpoint identifier: path only (query and
 * hash dropped — several call sites inline user-typed search terms into the
 * query string), with record UUIDs replaced so errors group by route rather
 * than by instance. Sentry tags/fingerprints are not scrubbed by beforeSend,
 * so this value must be safe at the source.
 *
 * @example
 * toPathOnlyEndpoint("/admin/superadmin/users?search=jane@example.com")
 * // => "/admin/superadmin/users"
 * toPathOnlyEndpoint("/campaigns/3f8a1c2e-9b4d-4e6f-8a2b-1c9d8e7f6a5b/leads")
 * // => "/campaigns/:id/leads"
 */
function toPathOnlyEndpoint(rawUrl: string | undefined): string {
  if (!rawUrl) return "unknown";
  let path: string;
  try {
    path = new URL(rawUrl, window.location.origin).pathname;
  } catch {
    path = rawUrl.split(/[?#]/)[0];
  }
  return path.replace(/\/[a-f0-9-]{36}/g, "/:id");
}

// Track if we're already redirecting to the 503 page (prevents multiple redirects)
let isRedirectingToMaintenance = false;

// One in-flight health check shared by every request that fails at once
// (a dead backend fails all of a page's requests together).
let healthCheckInFlight: null | Promise<boolean> = null;

function redirectToServiceUnavailable(reason: ServiceUnavailableReason): void {
  if (isRedirectingToMaintenance) return;
  isRedirectingToMaintenance = true;
  window.location.href = buildServiceUnavailableUrl(reason, window.location.pathname);
}

function confirmOutageThenRedirect(): void {
  if (isRedirectingToMaintenance || healthCheckInFlight) return;
  healthCheckInFlight = isApiReachable(apiBaseURL).finally(() => {
    healthCheckInFlight = null;
  });
  void healthCheckInFlight.then((reachable) => {
    // Re-check the path: the user may have reached the 503 page while the ping ran.
    if (!reachable && window.location.pathname !== SERVICE_UNAVAILABLE_PATH) {
      redirectToServiceUnavailable("outage");
    }
  });
}

// Prevent concurrent refresh attempts — the first 401 triggers a refresh and
// subsequent 401s queue up waiting for the same refresh to finish.
let refreshInProgress: null | Promise<boolean> = null;

// Response interceptor for handling errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest.url;

    // RAP-220: send the user to the 503 screen when the API is unavailable.
    // A 503 redirects at once; a network error or ALB 502/504 is confirmed
    // with a health ping first (see classifyApiFailure for what is ignored).
    // Routes can opt out by setting config.skipMaintenanceRedirect = true when
    // they use 503 to signal a business-logic condition (e.g. stage-categories
    // save timeouts return 503 with a typed error code).
    const failureAction = classifyApiFailure({
      code: error.code,
      currentPath: window.location.pathname,
      isCancel: axios.isCancel(error),
      online: navigator.onLine,
      skipMaintenanceRedirect: originalRequest?.skipMaintenanceRedirect === true,
      status: error.response?.status,
    });
    if (failureAction === "redirect") {
      redirectToServiceUnavailable(
        reasonFromResponse(error.response?.status, error.response?.data)
      );
      return Promise.reject(error);
    }
    if (failureAction === "confirm") {
      // Don't hold the caller's rejection on the ping; fall through so the
      // failure is still tracked and captured below.
      confirmOutageThenRedirect();
    }

    // If error is 401 and not already retrying and not an auth endpoint
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !requestUrl.includes("/auth/login") &&
      !requestUrl.includes("/auth/refresh")
    ) {
      originalRequest._retry = true;

      const authStore = useAuthStore();

      // Deduplicate: if a refresh is already in flight, wait for it
      if (!refreshInProgress) {
        refreshInProgress = authStore.refreshAccessToken().finally(() => {
          refreshInProgress = null;
        });
      }

      try {
        const success = await refreshInProgress;

        if (success && authStore.accessToken) {
          // Retry the original request with the new token
          originalRequest.headers.Authorization = `Bearer ${authStore.accessToken}`;
          return apiClient(originalRequest);
        }

        // Refresh returned false. Two possible reasons:
        //  a) Auth rejection (401/403) — resetAuth() was called, isAuthenticated is now false.
        //     → Redirect to login.
        //  b) Transient failure (network/5xx) — session is preserved, background retry
        //     is scheduled. Don't redirect; just let this request fail so the component
        //     can show an error. The next user action (or the background retry) will
        //     recover when the backend is back.
        if (!authStore.isAuthenticated) {
          const currentPath = window.location.pathname;
          if (!currentPath.startsWith("/auth/")) {
            const fullPath = currentPath + window.location.search;
            window.location.href = `/auth/login?redirect=${encodeURIComponent(fullPath)}`;
          }
        }

        return Promise.reject(error);
      } catch (_refreshError) {
        return Promise.reject(error);
      }
    }

    // Normalize once for both analytics + Sentry (see toPathOnlyEndpoint).
    const statusCode = error.response?.status;
    const endpoint = toPathOnlyEndpoint(originalRequest.url);
    const method = originalRequest.method?.toUpperCase();

    // Analytics: Track API errors (excluding auth refresh attempts)
    if (statusCode && statusCode >= 400 && !originalRequest._retry) {
      trackEvent("Error Occurred", {
        endpoint,
        errorCategory: statusCode >= 500 ? "server_error" : "client_error",
        method,
        statusCode,
      });
    }

    // Sentry: capture only high-signal failures — 5xx server errors and genuine
    // network failures. Expected 4xx (auth/permission/validation) are noise and
    // skipped; user-cancelled/navigation-aborted requests are not real errors.
    const isServerError = !!statusCode && statusCode >= 500;
    const isNetworkError = !error.response && !axios.isCancel(error);
    if (isServerError || isNetworkError) {
      captureApiError({ endpoint, method, statusCode });
    }

    return Promise.reject(error);
  }
);

export default apiClient;
